import useCustomerSubscriptionMutations from "features/subscriptions/datahooks/useCustomerSubscriptionMutations";
import useWorkspaceSubscriptionMutations from "features/subscriptions/datahooks/useWorkspaceSubscriptionMutations";
import { trackSubscriptionEvent } from "features/subscriptions/utils";

import type {
  DetailedWorkspace,
  RentedSubscription,
  Subscription,
  SubscriptionType,
} from "common/types";

function getCreatedSubscriptionId({
  workspace,
  subscription,
  subscriptionType,
}: {
  workspace?: DetailedWorkspace;
  subscription?: Subscription | RentedSubscription;
  subscriptionType: SubscriptionType;
}): string | undefined {
  if (subscription?.id) {
    return subscription.id;
  }

  if (!workspace) {
    return undefined;
  }

  if (subscriptionType === "rental") {
    return (
      workspace.rental_subscriptions?.at(-1)?.id ??
      workspace.rental_subscriptions?.[0]?.id
    );
  }

  return workspace.subscription?.id;
}

export default function useCreateSubscription(
  subscriptionType: SubscriptionType,
  couponId: string | undefined,
) {
  const {
    createCustomerSubscription,
    isCreatingCustomerSubscription,
    createCustomerSubscriptionError,
  } = useCustomerSubscriptionMutations();
  const {
    createWorkspaceSubscription,
    isCreatingWorkspaceSubscription,
    createWorkspaceSubscriptionError,
  } = useWorkspaceSubscriptionMutations();

  async function createSubscription({
    quantity,
    planId,
    paymentSourceId,
    planValue,
  }: {
    quantity: number;
    planId: string;
    paymentSourceId: string;
    planValue: number;
  }) {
    let subscriptionId: string;
    if (subscriptionType === "agency") {
      const { subscription } = await createCustomerSubscription({
        count: quantity,
        paymentSourceId,
        planPricingId: planId,
        couponId,
      });
      subscriptionId = subscription.id;
    } else {
      const { workspace, subscription } = await createWorkspaceSubscription({
        count: quantity,
        planId,
        paymentSourceId,
        type: subscriptionType,
        couponId,
      });
      subscriptionId = getCreatedSubscriptionId({
        workspace,
        subscription,
        subscriptionType,
      });
    }

    if (subscriptionId) {
      trackSubscriptionEvent("subscribe", subscriptionId, planValue, quantity);
    }
  }

  return {
    createSubscription,
    isCreatingSubscription:
      isCreatingCustomerSubscription || isCreatingWorkspaceSubscription,
    createSubscriptionError:
      createCustomerSubscriptionError || createWorkspaceSubscriptionError,
  };
}
